import type { UIMessage } from 'ai';
import { useCallback, useEffect, useRef, useState } from 'react';

import { shelfList, shelfLoad, shelfStore, type ShelfEntry } from '@/shared/api/lotus/shelf';
import { logger } from '@/shared/lib';

export interface ThreadSummary {
  key: string;
  title: string;
  createdAt: number | null;
}

function threadKey(threadId: string): string {
  return `copilot_thread:${threadId}`;
}

function extractTitle(messages: UIMessage[]): string {
  const firstUserMsg = messages.find((m) => m.role === 'user');
  if (!firstUserMsg) return 'New thread';

  const textParts = firstUserMsg.parts?.filter((p) => p.type === 'text') ?? [];
  const raw = textParts.map((p) => (p as { text: string }).text).join(' ');
  const sentences = raw.split(/(?<=[.!?])\s+/);
  const preview = sentences.slice(0, 2).join(' ');
  return preview.length > 80 ? `${preview.slice(0, 77)}...` : preview || 'New thread';
}

function generateThreadId(): string {
  return `t_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function parseThreadCreatedAt(threadKey: string): number | null {
  const match = threadKey.match(/^t_(\d+)_/);
  if (!match) return null;
  const ms = Number(match[1]);
  return Number.isFinite(ms) ? ms : null;
}

export interface UseCopilotThreadsOptions {
  userId: string;
}

export function useCopilotThreads({ userId }: UseCopilotThreadsOptions) {
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string>(() => generateThreadId());
  const [isLoading, setIsLoading] = useState(false);
  const savedRef = useRef(false);

  const refreshList = useCallback(async () => {
    try {
      const entries: ShelfEntry[] = await shelfList(userId);
      const threadEntries = entries.filter((e) => e.key.startsWith('copilot_thread:'));
      const nextThreads = threadEntries
        .map((e) => {
          const key = e.key.replace('copilot_thread:', '');
          return {
            key,
            title: (e.metadata as { title?: string }).title ?? 'Untitled',
            createdAt: parseThreadCreatedAt(key),
          };
        })
        .sort((a, b) => {
          const aCreatedAt = a.createdAt ?? Number.MIN_SAFE_INTEGER;
          const bCreatedAt = b.createdAt ?? Number.MIN_SAFE_INTEGER;
          return bCreatedAt - aCreatedAt;
        });
      setThreads(nextThreads);
    } catch (err) {
      logger.error('copilot.threads.list.failed', {}, err as Error);
    }
  }, [userId]);

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  const saveThread = useCallback(
    async (threadId: string, messages: UIMessage[]) => {
      if (messages.length === 0) return;
      const title = extractTitle(messages);
      try {
        await shelfStore(
          userId,
          threadKey(threadId),
          messages as unknown as Array<Record<string, unknown>>,
          { title }
        );
        savedRef.current = true;
        void refreshList();
      } catch (err) {
        logger.error('copilot.threads.save.failed', { threadId }, err as Error);
      }
    },
    [userId, refreshList]
  );

  const loadThread = useCallback(
    async (threadId: string): Promise<UIMessage[] | null> => {
      setIsLoading(true);
      try {
        const result = await shelfLoad(userId, threadKey(threadId));
        return result.value as unknown as UIMessage[];
      } catch {
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [userId]
  );

  const switchThread = useCallback(
    async (
      threadId: string,
      currentMessages: UIMessage[],
      setMessages: (messages: UIMessage[]) => void
    ) => {
      if (currentMessages.length > 0) {
        await saveThread(activeThreadId, currentMessages);
      }

      setActiveThreadId(threadId);
      const loaded = await loadThread(threadId);
      setMessages(loaded ?? []);
    },
    [activeThreadId, saveThread, loadThread]
  );

  const createNewThread = useCallback(
    async (currentMessages: UIMessage[], setMessages: (messages: UIMessage[]) => void) => {
      if (currentMessages.length > 0) {
        await saveThread(activeThreadId, currentMessages);
      }

      const newId = generateThreadId();
      setActiveThreadId(newId);
      setMessages([]);
    },
    [activeThreadId, saveThread]
  );

  return {
    threads,
    activeThreadId,
    isLoading,
    saveThread,
    loadThread,
    switchThread,
    createNewThread,
    refreshList,
  };
}
